import { ReactNode } from "react";

import { useTranslation } from "react-i18next";

import Modal from "../../../layouts/Modal";
import classNames from "../../../utils/classNames";
import { ButtonInput } from "../Inputs/ButtonInput";
import LoadingWheel from "../LoadingWheel";
import { IMergeStat } from "./MergeColumn";

interface MergeLayoutProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  title: string;
  intro: string;
  // The search that adds another row to the merge.
  search: ReactNode;
  // One MergeColumn per row, the kept one first.
  columns: ReactNode[];
  // Something other than the kept row has been picked.
  hasSelection: boolean;
  emptyText: string;
  isLoading: boolean;
  isError: boolean;
  // Account clashes, the bank details choice - anything the admin must read before confirming.
  notices?: ReactNode;
  // What moves onto the kept row, totalled across everything merged in.
  moving?: IMergeStat[];
  keptName?: string;
  fieldsFilled?: string[];
  refusal?: string | null;
  confirmLabel: string;
  confirmDisabled: boolean;
  isConfirming: boolean;
  onConfirm: () => void;
}

// The frame both merge dialogs share - contacts from a contact page, accounts from an account page -
// so merging reads the same everywhere: the rows side by side, kept first, then what will happen.
export default function MergeLayout({
  open,
  setOpen,
  title,
  intro,
  search,
  columns,
  hasSelection,
  emptyText,
  isLoading,
  isError,
  notices,
  moving = [],
  keptName,
  fieldsFilled = [],
  refusal,
  confirmLabel,
  confirmDisabled,
  isConfirming,
  onConfirm,
}: MergeLayoutProps) {
  const { t } = useTranslation("accounts");
  const movingNow = moving.filter((m) => m.count > 0);

  return (
    <Modal open={open} setOpen={setOpen} width="max-w-6xl">
      <div className="space-y-5">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          <p className="mt-1 text-sm text-gray-600">{intro}</p>
        </div>

        {search}

        {!hasSelection ? (
          <p className="rounded-md border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">{emptyText}</p>
        ) : isLoading ? (
          <div className="min-h-[16rem]">
            <LoadingWheel />
          </div>
        ) : isError ? (
          <p className="rounded-md bg-red-50 p-3 text-sm text-red-700">{t("Could not load the merge preview.")}</p>
        ) : (
          <div
            className={classNames(
              "grid max-h-[60vh] gap-4 overflow-y-auto p-0.5",
              columns.length === 2 ? "md:grid-cols-2" : "md:grid-cols-2 xl:grid-cols-3"
            )}
          >
            {columns}
          </div>
        )}

        {hasSelection && !isLoading && !isError && (
          <div className="space-y-3">
            {notices}

            {(movingNow.length > 0 || fieldsFilled.length > 0) && (
              <div className="rounded-md bg-gray-50 p-3 text-sm text-gray-700">
                {movingNow.length > 0 && (
                  <p>
                    <span className="font-medium">{t("Moving onto {{name}}:", { name: keptName ?? t("the kept one") })}</span>{" "}
                    {movingNow.map((m) => `${m.label} ${m.count}`).join(" · ")}
                  </p>
                )}
                {fieldsFilled.length > 0 && (
                  <p className="mt-1">
                    <span className="font-medium">{t("Blanks filled:")}</span> {fieldsFilled.join(", ")}.{" "}
                    {t("Everything else the kept one has stays as it is.")}
                  </p>
                )}
              </div>
            )}

            {refusal && <p className="rounded-md bg-red-50 p-3 text-sm text-red-700">{refusal}</p>}
          </div>
        )}

        <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
          <ButtonInput
            label={t("Cancel")}
            isSubmit={false}
            onClick={() => setOpen(false)}
            classes="border border-gray-300 !bg-white !text-gray-700 hover:!bg-gray-50"
          />
          <ButtonInput label={confirmLabel} isSubmit={false} onClick={onConfirm} classes="" disabled={confirmDisabled} isLoading={isConfirming} />
        </div>
      </div>
    </Modal>
  );
}
